// Client Portal — DataMap portal. Extracted from App.js.
import { useState, useEffect } from "react";
import { Activity, Loader2, Map, RefreshCw, Send } from "lucide-react";
import { authFetch } from "../lib/api";
import { showToast } from "../lib/toast";

export const DataMapPortal = ({ facility, user }) => {
  const fid = facility.facility_id;
  const [tab, setTab] = useState("datamaps");
  const [datamaps, setDatamaps] = useState([]);
  const [indicators, setIndicators] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMap, setSelectedMap] = useState(null);
  const [loadingIndicators, setLoadingIndicators] = useState(false);

  const isAdmin =
    facility.is_facility_admin ||
    facility.services?.find((s) => s.service_type === "datamap")
      ?.access_role === "admin";

  const tabs = [
    { id: "datamaps", label: "My Data Maps", icon: Map },
    { id: "indicators", label: "Indicators", icon: Activity },
    ...(isAdmin
      ? [{ id: "delivery", label: "Delivery Settings", icon: Send }]
      : []),
  ];

  useEffect(() => {
    fetchDatamaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDatamaps = async () => {
    setLoading(true);
    try {
      const data = await authFetch(`/datamap/list?facility_id=${fid}`);
      setDatamaps(Array.isArray(data) ? data : data.datamaps || []);
    } catch {
      setDatamaps([]);
      showToast("Failed to load data maps. Please try again.");
    }
    setLoading(false);
  };

  const fetchIndicators = async (datamapId) => {
    if (indicators[datamapId]) return;
    setLoadingIndicators(true);
    try {
      const data = await authFetch(
        `/datamap/indicators?datamap_id=${datamapId}`,
      );
      setIndicators((prev) => ({
        ...prev,
        [datamapId]: Array.isArray(data) ? data : data.indicators || [],
      }));
    } catch {
      // Don't cache the failure — re-selecting the map retries the fetch.
      showToast("Failed to load indicators. Please try again.");
    }
    setLoadingIndicators(false);
  };

  const selectMap = (dm) => {
    setSelectedMap(dm);
    fetchIndicators(dm.id);
  };

  const FreqBadge = ({ freq }) => {
    const colors = {
      Daily: "bg-blue-100 text-blue-700",
      Weekly: "bg-green-100 text-green-700",
      Monthly: "bg-purple-100 text-purple-700",
      Quarterly: "bg-amber-100 text-amber-700",
    };
    return (
      <span
        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${colors[freq] || "bg-slate-100 text-slate-700"}`}
      >
        {freq || "Custom"}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex space-x-1 bg-white rounded-lg shadow-sm border border-slate-200 p-1 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              tab === t.id
                ? "bg-emerald-600 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <t.icon className="w-4 h-4 mr-2" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "datamaps" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Datamap list */}
          <div className="lg:col-span-1 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">
                Data Maps ({datamaps.length})
              </h3>
              <button
                onClick={fetchDatamaps}
                className="p-1.5 text-slate-400 hover:text-emerald-600 rounded-lg"
              >
                <RefreshCw
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
              </button>
            </div>
            {loading ? (
              <div className="text-center py-8 text-sm text-slate-400">
                Loading data maps...
              </div>
            ) : datamaps.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                <Map className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">
                  No data maps assigned yet.
                </p>
              </div>
            ) : (
              datamaps.map((dm) => (
                <button
                  key={dm.id}
                  onClick={() => selectMap(dm)}
                  className={`w-full text-left bg-white rounded-xl border p-4 transition-all hover:shadow-md ${
                    selectedMap?.id === dm.id
                      ? "border-emerald-500 ring-1 ring-emerald-500"
                      : "border-slate-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-slate-900">
                      {dm.name}
                    </h4>
                    <span
                      className={`w-2.5 h-2.5 rounded-full ${dm.active ? "bg-green-500" : "bg-slate-300"}`}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <FreqBadge freq={dm.frequency} />
                    {dm.deliveryMethod && (
                      <span className="text-xs text-slate-500">
                        via {dm.deliveryMethod}
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-2">
            {selectedMap ? (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">
                        {selectedMap.name}
                      </h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        {selectedMap.deliveryMethod} delivery ·{" "}
                        {selectedMap.frequency}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${selectedMap.active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}
                    >
                      {selectedMap.active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-3 gap-4 p-6 border-b border-slate-100">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-emerald-600">
                      {(indicators[selectedMap.id] || []).length}
                    </p>
                    <p className="text-xs text-slate-500">Indicators</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-slate-800">
                      {selectedMap.frequency || "—"}
                    </p>
                    <p className="text-xs text-slate-500">Frequency</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-slate-800 text-sm mt-1">
                      {selectedMap.destination || "—"}
                    </p>
                    <p className="text-xs text-slate-500">Destination</p>
                  </div>
                </div>

                {/* Indicators list */}
                <div className="p-6">
                  <h4 className="text-sm font-semibold text-slate-900 mb-3">
                    Associated Indicators
                  </h4>
                  {loadingIndicators ? (
                    <div className="flex items-center justify-center py-6 text-sm text-slate-400">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Loading indicators...
                    </div>
                  ) : (indicators[selectedMap.id] || []).length === 0 ? (
                    <p className="text-sm text-slate-400 py-4 text-center">
                      No indicators configured for this data map.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {(indicators[selectedMap.id] || []).map((ind) => (
                        <div
                          key={ind.id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-slate-800">
                              {ind.dataIndicator || ind.name}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              {ind.source && (
                                <span className="text-xs text-slate-500">
                                  Source: {ind.source}
                                </span>
                              )}
                              {ind.category && (
                                <span className="text-xs px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">
                                  {ind.category}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {ind.frequency && (
                              <FreqBadge freq={ind.frequency} />
                            )}
                            <span
                              className={`w-2 h-2 rounded-full ${ind.active ? "bg-green-500" : "bg-slate-300"}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <Map className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">
                  Select a data map to view details and indicators.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "indicators" && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-slate-900">
              All Indicators
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Across all data maps for {facility.facility_name}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">
                    Indicator
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">
                    Source
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">
                    Frequency
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {Object.values(indicators)
                  .flat()
                  .map((ind) => (
                    <tr key={ind.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-800">
                        {ind.dataIndicator || ind.name}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {ind.source || "—"}
                      </td>
                      <td className="px-4 py-3">
                        {ind.category ? (
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs">
                            {ind.category}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <FreqBadge freq={ind.frequency} />
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ind.active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}
                        >
                          {ind.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  ))}
                {Object.values(indicators).flat().length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-8 text-center text-slate-400"
                    >
                      Select a data map first to load its indicators.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "delivery" && isAdmin && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900">
                Delivery Configuration
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                View and manage delivery settings for each data map.
              </p>
            </div>
            {datamaps.length === 0 ? (
              <div className="px-6 py-8 text-center text-sm text-slate-400">
                No data maps to configure.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {datamaps.map((dm) => (
                  <div
                    key={dm.id}
                    className="px-6 py-4 flex items-center justify-between"
                  >
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-medium text-slate-900">
                        {dm.name}
                      </h4>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-slate-500">
                          Method: {dm.deliveryMethod || "Not set"}
                        </span>
                        <span className="text-xs text-slate-500">
                          Dest: {dm.destination || "Not set"}
                        </span>
                        <FreqBadge freq={dm.frequency} />
                      </div>
                    </div>
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-medium ${dm.active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}
                    >
                      {dm.active ? "Active" : "Paused"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-xs text-blue-700">
              To modify delivery destinations or methods, contact your
              administrator.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// PODCAST PORTAL — client-facing podcast viewer
// ═══════════════════════════════════════════════════════════════════

// Podcast styles loaded from /podcast/styles at portal mount; seeded with a
// fallback so dropdowns render even before the fetch completes.
